/** QuickPlan — Gabarits de perçage 1:1 : découpage des faces percées en gabarits à découper.
 *
 * Chaque gabarit couvre une zone de la face (vue de l'extérieur, comme les plans de
 * perçage) recadrée au plus près de ses trous / encoches, mais calée autant que possible
 * sur les bords de la pièce : on le pose en alignant le trait épais sur le chant indiqué.
 * Sinon la consigne donne la distance du coin du gabarit aux chants. Plusieurs gabarits
 * sont rangés sur une même page A4 (templatesPdf.js), à découper le long du pointillé.
 * Une pièce en plusieurs exemplaires (×2) n'a qu'un jeu de gabarits, réutilisé.
 *
 * Les LIGNES RÉGULIÈRES de trous (taquets : au moins 5 trous au même pas sur une même
 * verticale) se percent avec une RÉGLETTE : bande à la même distance du chant, quelques
 * trous au pas, que l'on fait glisser en engageant son trou 1 dans le dernier trou percé.
 * Une réglette sert à toutes les lignes de même pas, même diamètre et même distance au chant.
 */
import { faceEdges } from '../../shared/notice.js';

/** Plus grande zone d'un gabarit (mm) : page A4 portrait ou paysage, en-têtes déduits. */
export const WINS = { portrait: { w: 182, h: 214 }, landscape: { w: 269, h: 128 } };
const MARGIN = 12; // distance mini d'une marque au bord du gabarit
const MIN_W = 64; // gabarit assez large pour sa consigne
const MIN_H = 30;
const EPS = 0.01;
const LINE_MIN = 5; // trous au même pas pour faire une ligne (réglette)
const TOL = 0.3;
const r1 = (v) => Math.round(v * 10) / 10;

/** Lignes régulières de trous d'une face : { lines: [{ u, v0, pitch, count, dia, depth,
 *  through, what, holes }], rest: trous hors lignes }. Les trous qui ne tombent pas au pas
 *  (taquet décalé…) n'interrompent pas une ligne : ils restent à part. */
export function holeLines(face) {
  const cols = new Map();
  for (const h of face.holes) {
    const k = `${r1(h.u)}|${h.dia}|${h.depth}|${h.through}`;
    if (!cols.has(k)) cols.set(k, []);
    cols.get(k).push(h);
  }
  const lines = [];
  const used = new Set();
  for (const col of cols.values()) {
    col.sort((a, b) => a.v - b.v);
    for (let i = 0; i < col.length; i++) {
      if (used.has(col[i])) continue;
      // Pas candidats : écarts aux quelques trous suivants ; on garde la plus longue suite.
      let best = null;
      for (let j = i + 1; j < Math.min(col.length, i + 4); j++) {
        const pitch = col[j].v - col[i].v;
        if (pitch < 8) continue;
        const run = [col[i]];
        let next = col[i].v + pitch;
        for (let m = j; m < col.length; m++) {
          if (used.has(col[m])) continue;
          if (Math.abs(col[m].v - next) < TOL) { run.push(col[m]); next += pitch; } else if (col[m].v > next + TOL) break;
        }
        if (!best || run.length > best.run.length) best = { pitch, run };
      }
      if (best && best.run.length >= LINE_MIN) {
        best.run.forEach((h) => used.add(h));
        const h0 = best.run[0];
        lines.push({ u: h0.u, v0: h0.v, pitch: r1(best.pitch), count: best.run.length, dia: h0.dia, depth: h0.depth, through: h0.through, what: h0.what, holes: best.run });
      }
    }
  }
  return { lines, rest: face.holes.filter((h) => !used.has(h)) };
}

/** Marques à couvrir d'une face : trous (avec leur rayon) et encoches (rectangles). */
function marksOf(face) {
  const out = [];
  for (const h of face.holes) out.push({ kind: 'hole', u0: h.u - h.dia / 2, u1: h.u + h.dia / 2, v0: h.v - h.dia / 2, v1: h.v + h.dia / 2, item: h });
  for (const r of face.recesses ?? []) out.push({ kind: 'recess', u0: r.u, u1: r.u + r.w, v0: r.v, v1: r.v + r.h, item: r });
  return out;
}

/** Début de zone sur un axe : calée sur le bord 0 si possible, sinon sur le bord L,
 *  sinon juste avant la marque. */
function placeAxis(lo, hi, L, win) {
  if (L <= win) return 0;
  if (hi + MARGIN <= win) return 0;
  if (lo - MARGIN >= L - win) return L - win;
  return Math.max(0, Math.min(L - win, lo - MARGIN));
}

const inside = (m, w) => m.u0 >= w.u0 - EPS && m.u1 <= w.u0 + w.w + EPS && m.v0 >= w.v0 - EPS && m.v1 <= w.v0 + w.h + EPS;

/** Regroupement des marques d'une face en zones (glouton : de gauche à droite, de bas en haut). */
export function faceWindows(face, WIN = WINS.portrait) {
  const [W, H] = face.size;
  let todo = marksOf(face);
  const wins = [];
  let guard = 0;
  while (todo.length && guard++ < 1000) {
    const p = todo.reduce((a, b) => (b.u0 < a.u0 - EPS || (Math.abs(b.u0 - a.u0) < EPS && b.v0 < a.v0) ? b : a));
    const u0 = placeAxis(p.u0, Math.min(p.u1, p.u0 + WIN.w - 2 * MARGIN), W, WIN.w);
    const col = todo.filter((m) => m.u0 >= u0 - EPS && m.u1 <= u0 + WIN.w + EPS);
    const q = (col.length ? col : [p]).reduce((a, b) => (b.v0 < a.v0 ? b : a));
    const v0 = placeAxis(q.v0, Math.min(q.v1, q.v0 + WIN.h - 2 * MARGIN), H, WIN.h);
    const win = { u0, v0, w: WIN.w, h: WIN.h };
    const got = todo.filter((m) => inside(m, win));
    if (!got.length) got.push(q); // marque plus grande qu'une page : posée tronquée
    win.marks = got;
    wins.push(shrink(win, W, H));
    todo = todo.filter((m) => !got.includes(m));
  }
  return wins;
}

/** Recadre une zone au plus près de ses marques, en gardant les chants sur lesquels elle
 *  était calée (ils servent à la poser), avec une taille minimale. */
function shrink(win, W, H) {
  const ms = win.marks;
  const axis = (lo0, size, L, mlo, mhi, MIN) => {
    const atLo = lo0 <= EPS;
    const atHi = lo0 + size >= L - EPS;
    let a = atLo ? 0 : Math.max(0, mlo - MARGIN);
    let b = atHi ? L : Math.min(L, mhi + MARGIN);
    if (b - a < MIN) {
      if (atLo && !atHi) b = a + MIN;
      else if (atHi && !atLo) a = Math.max(0, b - MIN);
      else { const c = (a + b) / 2; a = Math.max(0, c - MIN / 2); b = a + MIN; }
      if (b - a < MIN) b = a + MIN; // pièce plus petite que le gabarit : déborde (grisé)
    }
    return [a, b];
  };
  const [u0, u1] = axis(win.u0, win.w, W, Math.min(...ms.map((m) => m.u0)), Math.max(...ms.map((m) => m.u1)), MIN_W);
  const [v0, v1] = axis(win.v0, win.h, H, Math.min(...ms.map((m) => m.v0)), Math.max(...ms.map((m) => m.v1)), MIN_H);
  return { u0, v0, w: u1 - u0, h: v1 - v0, marks: ms };
}

/**
 * Tous les gabarits de la notice : zones { kind: 'window', id: 'G3', piece, face, part, parts,
 * u0, v0, w, h, holes, recesses, labels, edges } puis réglettes { kind: 'ruler', id: 'R1',
 * side, du, pitch, dia, depth, through, what, k, grad, uses: [{ piece, face, edge, bottom, v0, count }] }.
 * edges.* = libellé du chant si le bord de la pièce est sur le gabarit, sinon null.
 */
export function templateSheets(notice) {
  const sheets = [];
  const rulers = new Map();
  for (const p of notice.pieces) {
    for (const face0 of p.faces) {
      const labels = faceEdges(face0.code, p.size);
      const [W, H] = face0.size;
      const { lines, rest } = holeLines(face0);
      const kept = [];
      for (const l of lines) {
        const left = l.u <= W - l.u;
        const du = r1(left ? l.u : W - l.u);
        if (du > 140) { kept.push(...l.holes); continue; } // trop loin du chant pour une bande A4
        const key = `${left ? 'g' : 'd'}|${du}|${l.pitch}|${l.dia}|${l.depth}|${l.through}`;
        const r = rulers.get(key) ?? { kind: 'ruler', side: left ? 'left' : 'right', du, pitch: l.pitch, dia: l.dia, depth: l.depth, through: l.through, what: l.what, uses: [] };
        r.uses.push({ piece: p, face: face0, edge: left ? labels.left : labels.right, bottom: labels.bottom, v0: l.v0, count: l.count });
        rulers.set(key, r);
      }
      const face = { ...face0, holes: [...rest, ...kept] };
      if (!face.holes.length && !(face.recesses ?? []).length) continue;
      const port = faceWindows(face, WINS.portrait);
      const land = faceWindows(face, WINS.landscape);
      const wins = land.length < port.length ? land : port;
      wins.forEach((w, i) => {
        sheets.push({
          kind: 'window',
          piece: p,
          face: face0,
          part: i + 1,
          parts: wins.length,
          u0: w.u0, v0: w.v0, w: w.w, h: w.h,
          holes: w.marks.filter((m) => m.kind === 'hole').map((m) => m.item),
          recesses: w.marks.filter((m) => m.kind === 'recess').map((m) => m.item),
          labels,
          edges: {
            left: w.u0 <= EPS ? labels.left : null,
            right: w.u0 + w.w >= W - EPS ? labels.right : null,
            bottom: w.v0 <= EPS ? labels.bottom : null,
            top: w.v0 + w.h >= H - EPS ? labels.top : null,
          },
        });
      });
    }
  }
  sheets.forEach((s, i) => { s.no = i + 1; s.id = `G${i + 1}`; });
  // Réglettes : autant de trous que la page en contient, au plus la plus longue des lignes ;
  // graduation sous le trou 1 pour le caler depuis le chant bas.
  let n = 0;
  for (const r of rulers.values()) {
    const maxV0 = Math.max(...r.uses.map((u) => u.v0));
    r.grad = maxV0 + 8 <= 110 ? Math.ceil((maxV0 + 8) / 10) * 10 : 20;
    const room = 236 - r.grad - MARGIN;
    r.k = Math.max(2, Math.min(Math.max(...r.uses.map((u) => u.count)), Math.floor(room / r.pitch) + 1));
    n++;
    sheets.push({ ...r, no: sheets.length + 1, id: `R${n}` });
  }
  return sheets;
}

/** Gabarits d'une face : « gabarits G3 à G6 + réglette R1 ». */
export function sheetRange(sheets, piece, faceCode) {
  const ids = sheets.filter((s) => s.kind === 'window' && s.piece === piece && s.face.code === faceCode).map((s) => s.id);
  const rul = sheets.filter((s) => s.kind === 'ruler' && s.uses.some((u) => u.piece === piece && u.face.code === faceCode)).map((s) => s.id);
  const parts = [];
  if (ids.length) parts.push(ids.length === 1 ? `gabarit ${ids[0]}` : `gabarits ${ids[0]} à ${ids[ids.length - 1]}`);
  if (rul.length) parts.push(`réglette${rul.length > 1 ? 's' : ''} ${rul.join(', ')}`);
  return parts.join(' + ');
}

/** Consigne de pose d'un gabarit sur la pièce. */
export function placementText(sh) {
  const fmt = (v) => String(Math.round(v * 10) / 10).replace('.', ',');
  const [W, H] = sh.face.size;
  const lines = [];
  const lu = sh.edges.left ? `trait épais de gauche sur le ${sh.edges.left.toLowerCase()}`
    : sh.edges.right ? `trait épais de droite sur le ${sh.edges.right.toLowerCase()}`
      : `bord gauche du gabarit à ${fmt(sh.u0)} mm du ${sh.labels.left.toLowerCase()}`;
  const lv = sh.edges.bottom ? `trait épais du bas sur le ${sh.edges.bottom.toLowerCase()}`
    : sh.edges.top ? `trait épais du haut sur le ${sh.edges.top.toLowerCase()}`
      : `bas du gabarit à ${fmt(sh.v0)} mm du ${sh.labels.bottom.toLowerCase()}`;
  lines.push(`${lu.charAt(0).toUpperCase()}${lu.slice(1)} ; ${lv}.`);
  if (W < sh.u0 + sh.w - EPS || H < sh.v0 + sh.h - EPS) lines.push('Partie grisée : hors de la pièce.');
  return lines;
}
